import React, { useState } from 'react';
import styled from 'styled-components';
import {
    Box,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { FormField } from 'common/components/FormField';
import { TextField } from 'common/components/TextField';
import { CrossIcon } from 'common/components/icons';
import { SectionTitle } from 'common/components/SectionTitle';
import { useEmployeeFiltersQuery } from 'graphql/types';

interface IProps {
    className?: string;
    onBudgetSelect(budgetId: string): void;
    onCrossIconClick(): void;
    onSearch(searchTerm: string): void;
    onStatusSelect(status: string): void;
}

const BaseFilters: React.FC<IProps> = ({
    className,
    onBudgetSelect,
    onCrossIconClick,
    onSearch,
    onStatusSelect,
}) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const { data } = useEmployeeFiltersQuery();
    const { company, invitationStatusses } = data ?? {};

    const handleSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        // Two-way bind the input to the searchTerm state
        // Also call the callback, to trigger the debounced query
        setSearchTerm(e.target.value);
        onSearch(e.target.value);
    };

    return (
        <div className={className}>
            <Box p={5}>
                <SectionTitle>{t('employee.searchHeader')}</SectionTitle>
                <FormField maxWidth={300} mt={2}>
                    <TextField
                        id="search"
                        label={t('employee.search')}
                        value={searchTerm}
                        variant="filled"
                        onChange={handleSearch}
                    />
                </FormField>
                <SectionTitle>{t('employee.filter')}</SectionTitle>
                {company && company.budgets.length > 1 && (
                    <Grid container alignItems="center">
                        <Grid item lg={1} xs={12}>
                            <Typography variant="h6">Budget</Typography>
                        </Grid>
                        <Grid item lg={11} xs={12}>
                            <FormField>
                                <RadioGroup
                                    row
                                    aria-label="Budget"
                                    defaultValue=""
                                    name="budgetId"
                                    onChange={(e) =>
                                        onBudgetSelect(e.target.value)
                                    }
                                >
                                    <FormControlLabel
                                        control={<Radio color="default" />}
                                        label={t('employee.all')}
                                        value=""
                                    />
                                    {company?.budgets.map((budget) => (
                                        <FormControlLabel
                                            control={<Radio color="default" />}
                                            key={budget.id}
                                            label={budget.name}
                                            value={budget.id}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormField>
                        </Grid>
                    </Grid>
                )}
                {invitationStatusses && (
                    <Grid container alignItems="center">
                        <Grid item lg={1} xs={12}>
                            <Typography variant="h6">
                                {t('employee.status')}
                            </Typography>
                        </Grid>
                        <Grid item lg={11} xs={12}>
                            <RadioGroup
                                row
                                aria-label="Status"
                                defaultValue=""
                                name="status"
                                onChange={(e) => onStatusSelect(e.target.value)}
                            >
                                <FormControlLabel
                                    control={<Radio color="default" />}
                                    label="Alle"
                                    value=""
                                />
                                {invitationStatusses.map(([value, label]) => (
                                    <FormControlLabel
                                        control={<Radio color="default" />}
                                        key={value}
                                        label={label}
                                        value={value}
                                    />
                                ))}
                            </RadioGroup>
                        </Grid>
                    </Grid>
                )}
            </Box>
            <CrossIcon onClick={onCrossIconClick} />
        </div>
    );
};

export const Filters = styled(BaseFilters)`
    background-color: ${({ theme }) => theme.palette.grey[50]};
    position: relative;
    .MuiSvgIcon-root {
        width: 1.5em;
        height: 1.5em;
    }
    .MuiInputBase-input {
        background-color: ${({ theme }) => theme.palette.background.paper};
    }
    ${CrossIcon} {
        width: 16px;
        height: 16px;
        position: absolute;
        top: 10px;
        right: 10px;
        fill: ${({ theme }) => theme.palette.grey[900]};
        cursor: pointer;
    }
`;
